import { useContext, useState } from "react";

import { Delete } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import { deleteBulletinId } from "@rubi/api-generated";
import { colors } from "@rubi/ui";
import { useSearchParams } from "react-router-dom";

import { isSmallScreen } from "@/common/utils";
import { BulletinResourceResponse } from "@/types/typesAlerts";

import { AlertViewContext } from "./context";

const SORTABLE_FIELDS = new Set([
  "issued_time",
  "incident_time",
  "time_created",
  "time_updated",
]);

// Drafts are read straight off the bulletin table, so the plain columns can be
// sorted server-side as well.
const DRAFT_SORTABLE_FIELDS = new Set([
  ...SORTABLE_FIELDS,
  "id",
  "type",
  "classification",
  "operational_area",
  "approved_by",
  "approved_by_badge",
]);

/**
 * Creates columns for the DataGrid and applies default settings
 * @param fields
 * @param sortableFields fields the API can sort on
 * @returns
 */
const createColumns = (fields: GridColDef[], sortableFields: Set<string>) => {
  return fields.map((f: GridColDef) => ({
    width: 200,
    sortable: sortableFields.has(f.field),
    ...f,
  }));
};

const generateRowData = (bulletin: BulletinResourceResponse) => {
  const getAssociatedCrimes = () => {
    if (!bulletin.associated_crime) return "";
    return bulletin.associated_crime.join(", ");
  };
  const getOccurence = () => {
    if (!bulletin.occurrence_number) return "";
    return bulletin.occurrence_number.join(", ");
  };
  const getAddresses = () => {
    const addresses = bulletin.location.addresses;
    if (!addresses) return "";
    return addresses.map((address) => address.description).join(", ");
  };

  const issuedTime = new Date(bulletin.issued_time);

  const incidentTime = new Date(
    bulletin.incident_times.length > 0
      ? bulletin.incident_times[0].start_date
      : null,
  );
  const issuingPoliceName = bulletin.issuing_police_service?.name || "";
  const bulletinType = bulletin.type || "";
  const updatedDate = new Date(bulletin.time_updated || bulletin.time_created);

  const getTags = () => {
    if (!bulletin.tags) return "";
    return Object.values(bulletin.tags)
      .map((tag) => tag.title)
      .join(", ");
  };

  const rowData = {
    id: bulletin.id,
    issued_time: issuedTime,
    incident_time: incidentTime,
    issuing_police_service: issuingPoliceName,
    type: bulletinType,
    location: getAddresses(),
    associated_crime: getAssociatedCrimes(),
    occurence: getOccurence(),
    approved_by: bulletin.approved_by,
    approved_by_badge: bulletin.approved_by_badge,
    classification: bulletin.classification,
    operational_area: bulletin.operational_area,
    tags: getTags(),
    time_updated: updatedDate,
    time_created: new Date(bulletin.time_created),
  };
  return rowData;
};

interface IAlertTableProps {
  alertClickHandler: (item: any, index: any) => void;
  loadMore: () => void;
  isDraftsList: boolean;
  refetch: () => void;
  selectedAlertId?: number | null;
}

const AlertTable = ({
  alertClickHandler,
  loadMore,
  isDraftsList,
  refetch,
  selectedAlertId,
}: IAlertTableProps) => {
  const { totalAlerts, currentItems } = useContext(AlertViewContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteID, setDeleteID] = useState("");

  const sortableFields = isDraftsList ? DRAFT_SORTABLE_FIELDS : SORTABLE_FIELDS;

  const sortBy = searchParams.get("sort_by");
  const sortOrder = searchParams.get("sort_order");
  const sortModel: GridSortModel =
    sortBy && sortableFields.has(sortBy)
      ? [{ field: sortBy, sort: sortOrder === "asc" ? "asc" : "desc" }]
      : [];

  const handleSortModelChange = (model: GridSortModel) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (model.length === 0 || !model[0].sort) {
        next.delete("sort_by");
        next.delete("sort_order");
      } else {
        next.set("sort_by", model[0].field);
        next.set("sort_order", model[0].sort);
      }
      return next;
    });
  };

  const processDeletion = async () => {
    await deleteBulletinId({ path: { bulletin_id: Number(deleteID) } });
    refetch();
    setDeleteID("");
    setDeleteOpen(false);
  };

  const rows = currentItems
    .filter(
      (bulletin) =>
        (isDraftsList && bulletin.is_draft) ||
        (!isDraftsList && !bulletin.is_draft),
    )
    .map((bulletin) => generateRowData(bulletin));

  const fields: GridColDef[] = [];

  if (isDraftsList) {
    fields.push({
      field: "delete",
      headerName: "Delete",
      width: 100,
      renderCell: (params) => {
        const id = params.id;
        return (
          <IconButton
            onClick={() => {
              setDeleteID(id.toString());
              setDeleteOpen(true);
            }}
          >
            <Delete />
          </IconButton>
        );
      },
      disableColumnMenu: true,
    });
  }

  fields.push(
    {
      field: "id",
      headerName: "ID",
      width: 100,
      renderCell: (params) => {
        const id = params.id;
        return (
          <div
            style={{ cursor: "pointer", color: colors.primary.main }}
            onClick={() =>
              alertClickHandler(
                currentItems.find((item) => item.id === id),
                currentItems.findIndex((item) => item.id === id),
              )
            }
          >
            {id}
          </div>
        );
      },
      disableColumnMenu: true,
    },
    {
      field: "occurence",
      headerName: "Occurrence",
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "time_updated",
      headerName: "Last Updated",
      type: "dateTime",
      sortingOrder: ["desc", "asc", null],
      disableColumnMenu: true,
    },
    {
      field: "time_created",
      headerName: "Time Created",
      type: "dateTime",
      sortingOrder: ["desc", "asc", null],
      disableColumnMenu: true,
    },
    {
      field: "issued_time",
      headerName: "Issued Time",
      type: "dateTime",
      sortingOrder: ["desc", "asc", null],
      disableColumnMenu: true,
    },
    {
      field: "issuing_police_service",
      headerName: "Issuing Police Service",
      filterable: true,
      disableColumnMenu: true,
    },
    { field: "type", headerName: "Type", width: 200, disableColumnMenu: true },
    {
      field: "location",
      headerName: "Location",
      filterable: true,
      width: 300,
      disableColumnMenu: true,
    },
    {
      field: "associated_crime",
      headerName: "Associated Crime",
      disableColumnMenu: true,
    },
    {
      field: "incident_time",
      headerName: "Incident Time",
      type: "dateTime",
      sortingOrder: ["desc", "asc", null],
      disableColumnMenu: true,
    },
    {
      field: "approved_by",
      headerName: "Approved By",
      filterable: true,
      disableColumnMenu: true,
    },
    {
      field: "approved_by_badge",
      headerName: "Approved By Badge",
      filterable: true,
      disableColumnMenu: true,
    },
    {
      field: "classification",
      headerName: "Classification",
      filterable: true,
      disableColumnMenu: true,
    },
    {
      field: "operational_area",
      headerName: "Operational Area",
      filterable: true,
      disableColumnMenu: true,
    },
    {
      field: "tags",
      headerName: "Tags",
      sortable: false,
      filterable: true,
      disableColumnMenu: true,
    },
  );

  const columns = createColumns(fields, sortableFields);
  const mobileView = isSmallScreen();

  const mobileViewStyle = {
    maxHeight: "100vh",
    paddingTop: "16px",
    width: "95vw",
  };

  const desktopStyle = {
    paddingTop: "16px",
    width: "calc(100% - 32px)",
    marginLeft: "16px",
    marginRight: "16px",
    height: "calc(100vh)",
  };

  return (
    <div style={mobileView ? mobileViewStyle : desktopStyle}>
      <DataGrid
        rows={rows}
        columns={columns}
        disableVirtualization
        autoPageSize
        rowSelection={false}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        sx={{
          "& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)>.MuiDataGrid-cell":
            {
              whiteSpace: "normal",
              wordWrap: "break-word",
            },
        }}
        onPaginationModelChange={loadMore}
        rowCount={totalAlerts}
        getRowClassName={(params) => {
          if (selectedAlertId && selectedAlertId === params.id) {
            return "slideout-alert-selected-row";
          }
          return "slideout-alert-row";
        }}
        onRowClick={(params) => {
          alertClickHandler(
            currentItems.find((item) => item.id === params.id),
            currentItems.findIndex((item) => item.id === params.id),
          );
        }}
        onCellClick={(params, event) => {
          if (params.field === "delete") {
            event.stopPropagation();
            setDeleteID(params.id.toString());
            setDeleteOpen(true);
          }
        }}
      />
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Delete Draft Alert?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete draft bulletin with ID {deleteID}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>No</Button>
          <Button onClick={processDeletion} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AlertTable;
